import { GameController } from "../game/controller";
import { Play } from "../engine/moves";
import { buildHintTable, HintRow } from "./hint-table";
import { iconMarkup } from "./icons";
import { installModalBehavior, installPeek } from "./modal";
import { rankDistinctPlays } from "./ranked-plays";

export interface HintModalOpts {
  /** Push hint arrows onto the board. Pass null to clear. */
  setPreviewHint: (moves: { from: number; to: number }[] | null) => void;
}

export async function openHintModal(
  controller: GameController,
  overlayContainer: HTMLElement,
  opts: HintModalOpts,
): Promise<void> {
  const s = controller.state;
  if (s.phase.kind !== "play") return;
  const legalPlays = s.phase.legalPlays;
  if (legalPlays.length === 0 || (legalPlays.length === 1 && legalPlays[0].length === 0)) {
    return;
  }

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop hint-modal-backdrop board-anchored";
  const modal = document.createElement("div");
  modal.className = "modal hint-modal";
  modal.innerHTML = `
    <div class="modal-head">
      <h2>Best moves</h2>
      <button class="peek-btn" data-action="peek" type="button"
              title="Hold to see the board" aria-label="Hold to see the board">${iconMarkup("peek")}</button>
    </div>
    <div class="hint-body">Analyzing…</div>
    <div class="actions">
      <button data-action="play" class="primary" disabled>Play selected</button>
      <button data-action="close">Close</button>
    </div>
  `;
  backdrop.appendChild(modal);
  overlayContainer.appendChild(backdrop);

  let selectedPlay: Play | null = null;
  const playBtn = modal.querySelector<HTMLButtonElement>('[data-action="play"]')!;
  const closeBtn = modal.querySelector<HTMLButtonElement>('[data-action="close"]')!;

  const close = (): void => {
    opts.setPreviewHint(null);
    backdrop.remove();
  };
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) close();
  });
  installModalBehavior(backdrop, { onRequestClose: close });
  installPeek(modal, modal.querySelector<HTMLButtonElement>('[data-action="peek"]')!);
  playBtn.addEventListener("click", () => {
    if (!selectedPlay) return;
    const p = selectedPlay;
    close();
    controller.playFullPlay(p);
  });

  try {
    const analysisPromise = controller.getActiveAnalysis();
    if (!analysisPromise) throw new Error("No active analysis for this turn");
    const analysis = await analysisPromise;

    // Ranked best-first and deduped by final position — see rankDistinctPlays.
    // Ranking is by points equity, the same metric bestEquity is measured in,
    // so the loss column is consistent by construction.
    const ranked = rankDistinctPlays(
      s.position,
      legalPlays,
      analysis.pointsEquities,
      analysis.gameEquities,
    );
    // Equal to the top ranked row's equity by the AnalysisResult invariant.
    const bestEquity = analysis.bestEquity;

    const rows: HintRow[] = ranked.slice(0, 5).map((r) => ({
      play: r.play,
      equity: r.equity,
      winProb: r.winProb,
      rank: r.rank,
      isBest: r.rank === 1,
    }));

    const body = modal.querySelector<HTMLElement>(".hint-body")!;
    body.innerHTML = "";
    const table = buildHintTable({
      rows,
      bestEquity,
      onSelect: (row) => {
        selectedPlay = row.play;
        opts.setPreviewHint(row.play.map((sm) => ({ from: sm.from, to: sm.to })));
        playBtn.disabled = false;
      },
      defaultSelectIdx: 0,
    });
    body.appendChild(table);
  } catch (err) {
    const body = modal.querySelector<HTMLElement>(".hint-body")!;
    body.textContent = `Couldn't analyze: ${err instanceof Error ? err.message : String(err)}`;
  }
}
